import type {ReactNode} from "react";
import React, {useEffect, useState} from "react";
import styled, {css} from "styled-components";
import type {ComponentProps} from "widgets/BaseComponent";
import {generateClassName, getCanvasClassName} from "utils/generators";
import {scrollCSS} from "widgets/WidgetUtils";
import {Button, Steps} from "antd";
import "./index.css";
import _, {noop, some} from "lodash";
import {ButtonVariantTypes} from "../../../components/constants";
import { FontStyleTypes } from "design-system-old";
import tinycolor from "tinycolor2";
import {ButtonWrapper} from "../../TableWidget/component";
import {getIconPark} from "../../../components/propertyControls/IconSelectControl";
import {useSelector} from "react-redux";

export interface StepsComponentProps extends ComponentProps {
  children?: ReactNode;
  shouldScrollContents?: boolean;
  selectedTabWidgetId: string;
  shouldShowTabs: boolean;
  borderRadius: string;
  boxShadow?: string;
  borderWidth?: number;
  borderColor?: string;
  accentColor?: string;
  primaryColor: string;
  onTabChange: (tabId: string) => void;
  onFinish: () => void;
  onButtonClick: (params:any) => void;
  tabs: Array<{
    id: string;
    label: string;
    key: string;
    description: string;
    widgetId: string;
    isVisible?: boolean;
    index: number;
    buttons?: any;
  }>;
  width: number;
  $noScroll: boolean;
  selectedTab: string;
  showTabDesc: boolean;
  isBtnLoading?: boolean;
  stepDirection: string;
  labelPlacement: string;
}

const TabsContainerWrapper = styled.div<{
  borderRadius: string;
  boxShadow?: string;
  borderWidth?: number;
  borderColor?: string;
  backgroundColor?: string;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  justify-content: flex-start;
  align-items: center;
  border-radius: ${({borderRadius}) => borderRadius};
  box-shadow: ${({boxShadow}) => `${boxShadow}`} !important;
  border-width: ${(props) => props.borderWidth}px;
  border-color: ${(props) => props.borderColor || "transparent"};
  background: ${(props) =>
  props.backgroundColor || "var(--wds-color-bg)"};
  border-style: solid;
  overflow: hidden;
  padding: 10px;
`;

const ScrollCanvas = styled.div<{ $shouldScrollContents: boolean }>`
  overflow: hidden;
  ${(props) => (props.$shouldScrollContents ? scrollCSS : ``)}
  width: 100%;
`;

const StepButton = styled(Button)<any>`
  & {
    .ant-btn {
      display: flex;
      align-items: center;
      border-radius: ${({borderRadiusBtn}) => borderRadiusBtn} !important;
      box-shadow: ${({boxShadowBtn}) => boxShadowBtn} !important;
      color: ${({btnFontColor}) => btnFontColor} !important;
      font-size: ${({btnFontSize}) => btnFontSize || '0.875rem'} !important;
      height: inherit !important;
      min-height: 32px !important;
      font-style: ${({btnFontStyle}) => {
        return (btnFontStyle?.includes(FontStyleTypes.ITALIC) ? "italic" : "") + ' !important;'
      }};
            font-weight: ${({btnFontStyle}) => {
        return (btnFontStyle?.includes(FontStyleTypes.BOLD) ? "bold" : "") + ' !important;'
      }};
      .i-icon {
        ${({iconLeft}) => iconLeft ? 'margin-right:5px  !important;' : ''}
        ${({iconRight}) => iconRight ? 'margin-left:5px  !important;' : ''}
      }
    }

    .ant-btn[disabled], .ant-btn[disabled]:active, .ant-btn[disabled]:focus, .ant-btn[disabled]:hover {
      color: rgba(0,0,0,.25) !important;
      border: none !important;
      background: #f5f5f5 !important;
      text-shadow: none !important;
      box-shadow: none !important;
    }

    .ant-btn-default {
      border-color: ${({backgroundBtn}) => backgroundBtn} !important;
      &:hover{
        color: ${({btnFontColor}) => btnFontColor} !important;
        border-color: ${({backgroundBtn}) => backgroundBtn} !important;
        background: ${({backgroundBtn}) => backgroundBtn ? tinycolor(backgroundBtn).setAlpha(0.1).toRgbString() : ''};
      }
    }
    .ant-btn-primary{
      background: ${({backgroundBtn}) => backgroundBtn}!important;
      border: none!important;
      &:hover{
        opacity: .8;
      }
    }
    .ant-btn-text{
      border: none !important;
      &:hover{
        color: ${({btnFontColor}) => btnFontColor}!important;
        background: ${({backgroundBtn}) => tinycolor(backgroundBtn).setAlpha(0.1).toRgbString()};
      }
    }
  }
`

const CustomSteps = styled(Steps)<any>`
  ${({direction}) =>
  direction == 'vertical' &&
  css`
      max-width: 200px;
      flex: 1;
      min-width: 115px;
  `}

  .ant-steps-item-process .ant-steps-item-icon {
    background: ${({accentColor}) => accentColor};
  }

  .ant-steps-item-process .ant-steps-item-icon::after {
    background: ${({accentColor}) => accentColor};
  }

  .ant-steps-item-finish>.ant-steps-item-container>.ant-steps-item-content>.ant-steps-item-title:after {
    background: ${({accentColor}) => accentColor};
  }

  .ant-steps-item-finish .ant-steps-item-icon>.ant-steps-icon {
    border-color: ${({accentColor}) => accentColor};
    color: ${({accentColor}) => accentColor};
  }

  .ant-steps-item-finish .ant-steps-item-icon {
    border-color: ${({accentColor}) => accentColor};
  }

  .ant-steps-item-process .ant-steps-item-icon {
    border-color: ${({accentColor}) => accentColor};
  }

  .ant-steps-item-finish>.ant-steps-item-container>.ant-steps-item-tail:after  {
    background: ${({accentColor}) => accentColor};
  }

  .ant-steps-item-title {
    line-height: 26px !important;
  }
  .ant-steps-item-description {
    line-height: 20px !important;
  }
`;
const XBox = styled.div<{
  direction: string
}>`
  width: 100%;
  display: flex;
  height: calc(100% - 32px);
  flex-direction: ${({direction}) => direction == 'vertical' ? 'row' : 'column'};
`

function TabsComponent(props: StepsComponentProps) {
  const {onTabChange, tabs, onFinish, showTabDesc, selectedTabWidgetId, onButtonClick, isBtnLoading} = props;
  const [current, setCurrent] = useState<number>(0);
  const isValid:any = useSelector(state=> {
    let ret = false;
    let widgetIds:any = [];
    function fetchWidgetIds(widget:any) {
      widgetIds.push(widget.widgetId);
      if (widget.children && widget.children.length > 0) {
        widget.children.forEach((child:any) => {
          fetchWidgetIds(child);
        })
      }
    }

    // @ts-ignore
    (props.children?.props?.children || []).forEach((w:any) => {
      fetchWidgetIds(w);
    })

    Object.keys(state.evaluations.tree).forEach((key) => {
      if (!ret) {
        const value: any = state.evaluations.tree[key];
        if (widgetIds.indexOf(value.widgetId) > -1) {
          if ("isValid" in value) {
            ret = !value.isValid;
          }
        }
      }
    });
    return ret;
  });

  useEffect(()=>{
    if(selectedTabWidgetId){
      const selectedTabItemIndex: any = _.findIndex(tabs, { widgetId: selectedTabWidgetId });
      setCurrent(selectedTabItemIndex);
    }
  }, [selectedTabWidgetId]);

  const setCurrentFn = (value: number) => {
    setCurrent(value);
    onTabChange(tabs[value].widgetId);
  }
  const next = () => {
    setCurrentFn(current + 1);
  };

  const prev = () => {
    setCurrentFn(current - 1);
  };

  const items = tabs.map(item => {
    return {...item, key: item.label, title: item.label, description: showTabDesc ? item.description : ''}
  });

  return (
    <TabsContainerWrapper
      backgroundColor={props.backgroundColor}
      borderColor={props.borderColor}
      borderRadius={props.borderRadius}
      borderWidth={props.borderWidth}
      boxShadow={props.boxShadow}
    >
      <XBox direction={props.stepDirection}>
        <CustomSteps
          direction={props.stepDirection} labelPlacement={props.labelPlacement}
          accentColor={props.accentColor} current={current} items={items}/>

        <ScrollCanvas
          $shouldScrollContents={!!props.shouldScrollContents && !props.$noScroll}
          className={`${
            props.shouldScrollContents ? getCanvasClassName() : ""
          } ${generateClassName(props.widgetId)}`}
        >
          {props.children}
        </ScrollCanvas>
      </XBox>

      <div>
        {
          (tabs[current]?.buttons || []).map((item:any, index:any) => {
            if(item.btnIsVisible === false){
              return null;
            }
            if (item.label) {
              let type:any = 'default';
              let onClickEvent = noop;

              if (item.key == 'tabNextBtn') {
                type = 'primary';
                onClickEvent = next;
              } else if (item.key == 'tabFinishBtn') {
                type = 'primary';
                onClickEvent = onFinish;
              } else if (item.key == 'tabPrevBtn') {
                onClickEvent = prev;
              }

              if (item.buttonVariant == ButtonVariantTypes.PRIMARY) {
                type = 'primary';
              } else if (item.buttonVariant == ButtonVariantTypes.TERTIARY) {
                type = 'text';
              }

              const iconConfig:any = {}
              if (item.iconName) {
                iconConfig.icon = getIconPark(item.iconName)
              }


              let buttonStyle:any = {margin: '0 8px'};
              if (item.iconAlign == 'right') {
                buttonStyle.flexDirection = 'row-reverse';
              }

              return <ButtonWrapper
                borderRadiusBtn={item.borderRadius}
                backgroundBtn={item.buttonColor}
                boxShadowBtn={item.boxShadow}
                btnFontSize={item.fontSize}
                btnFontStyle={item.fontStyle}
                btnFontColor={item.textColor}
              >
                <Button
                  type={type}
                  key={index}
                  {...iconConfig}
                  style={{...buttonStyle}}
                  loading={isBtnLoading&&item.onClick}
                  disabled={(item.disabledWhenInvalid && isValid)||item.btnIsDisabled}
                  onClick={() => {
                    !isBtnLoading&&onButtonClick && onButtonClick(item.onClick);
                    onClickEvent && onClickEvent();
                  }}>
                  {item.label}
                </Button>
              </ButtonWrapper>
            }
          })
        }
      </div>

    </TabsContainerWrapper>
  );
}

export default TabsComponent;
